/**
 * Dashboard Grid Component
 * Main dashboard layout using react-grid-layout
 */

import { useCallback, useMemo, useRef, useState, useEffect, ComponentType } from 'react';
import GridLayout from 'react-grid-layout';
import { Plus } from 'lucide-react';
import type { Dashboard, Widget, WidgetData } from '../../types';
import { WidgetContainer } from './WidgetContainer';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

// Layout type from react-grid-layout
export interface LayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean;
}

interface DashboardGridProps {
  dashboard: Dashboard;
  widgetData: Record<string, WidgetData>;
  widgetLoading: Record<string, boolean>;
  widgetErrors: Record<string, string>;
  onLayoutChange?: (layout: LayoutItem[]) => void;
  onWidgetRefresh?: (widgetId: string) => void;
  onWidgetEdit?: (widget: Widget) => void;
  onWidgetDelete?: (widgetId: string) => void;
  onAddWidget?: () => void;
  isEditMode?: boolean;
}

const GRID_COLS = 12;
const ROW_HEIGHT = 80;
const MARGIN: [number, number] = [16, 16];

// Type the GridLayout component with correct props
type GridLayoutProps = {
  className?: string;
  layout: LayoutItem[];
  cols: number;
  rowHeight: number;
  width: number;
  margin: [number, number];
  isDraggable: boolean;
  isResizable: boolean;
  onLayoutChange: (layout: LayoutItem[]) => void;
  draggableHandle?: string;
  compactType?: 'vertical' | 'horizontal' | null;
  preventCollision?: boolean;
  children?: React.ReactNode;
};

const TypedGridLayout = GridLayout as unknown as ComponentType<GridLayoutProps>;

export function DashboardGrid({
  dashboard,
  widgetData,
  widgetLoading,
  widgetErrors,
  onLayoutChange,
  onWidgetRefresh,
  onWidgetEdit,
  onWidgetDelete,
  onAddWidget,
  isEditMode = false,
}: DashboardGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1200);

  // Track container width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Generate layout from widgets
  const layout = useMemo(() => {
    return dashboard.widgets.map((widget) => ({
      i: widget.grid_key || `widget-${widget.id}`,
      x: widget.position_x || 0,
      y: widget.position_y || 0,
      w: widget.width || 3,
      h: widget.height || 2,
      minW: 2,
      minH: 2,
    }));
  }, [dashboard.widgets]);

  // Map widgets by grid key for quick lookup
  const widgetsByKey = useMemo(() => {
    const map = new Map<string, Widget>();
    dashboard.widgets.forEach((widget) => {
      const key = widget.grid_key || `widget-${widget.id}`;
      map.set(key, widget);
    });
    return map;
  }, [dashboard.widgets]);

  const handleLayoutChange = useCallback(
    (newLayout: LayoutItem[]) => {
      if (isEditMode && onLayoutChange) {
        onLayoutChange(newLayout);
      }
    },
    [isEditMode, onLayoutChange]
  );

  if (dashboard.widgets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <p className="text-gray-500 text-lg mb-2">No widgets configured</p>
        <p className="text-gray-400 text-sm mb-4">
          Add widgets to start building your dashboard
        </p>
        {onAddWidget && (
          <button
            onClick={onAddWidget}
            className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Widget
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <TypedGridLayout
        className="layout"
        layout={layout}
        cols={GRID_COLS}
        rowHeight={ROW_HEIGHT}
        width={width}
        margin={MARGIN}
        isDraggable={isEditMode}
        isResizable={isEditMode}
        onLayoutChange={handleLayoutChange}
        draggableHandle=".cursor-move"
        compactType="vertical"
        preventCollision={false}
      >
        {layout.map((item) => {
          const widget = widgetsByKey.get(item.i);
          if (!widget) return null;

          return (
            <div key={item.i}>
              <WidgetContainer
                widget={widget}
                data={widgetData[widget.id]}
                loading={widgetLoading[widget.id]}
                error={widgetErrors[widget.id]}
                onRefresh={() => onWidgetRefresh?.(widget.id)}
                onEdit={() => onWidgetEdit?.(widget)}
                onDelete={() => onWidgetDelete?.(widget.id)}
                isEditMode={isEditMode}
              />
            </div>
          );
        })}
      </TypedGridLayout>
    </div>
  );
}
